import { gql, useMutation } from '@apollo/client';
import * as React from 'react';

import {
  useSetupIntentClientSecret_GetSetupIntentClientSecretForOrganization,
  useSetupIntentClientSecret_GetSetupIntentClientSecretForOrganizationVariables,
} from './graphql-types/useSetupIntentClientSecret_GetSetupIntentClientSecretForOrganization';
import {
  useSetupIntentClientSecret_GetSetupIntentClientSecretForUser,
  useSetupIntentClientSecret_GetSetupIntentClientSecretForUserVariables,
} from './graphql-types/useSetupIntentClientSecret_GetSetupIntentClientSecretForUser';

const GET_SETUP_INTENT_CLIENT_SECRET_FOR_USER = gql`
  mutation useSetupIntentClientSecret_GetSetupIntentClientSecretForUser(
    $input: GetSetupIntentClientSecretForUserInput!
  ) {
    getSetupIntentClientSecretForUser(input: $input) {
      clientSecret
    }
  }
`;

const GET_SETUP_INTENT_CLIENT_SECRET_FOR_ORGANIZATION = gql`
  mutation useSetupIntentClientSecret_GetSetupIntentClientSecretForOrganization(
    $input: GetSetupIntentClientSecretForOrganizationInput!
  ) {
    getSetupIntentClientSecretForOrganization(input: $input) {
      clientSecret
    }
  }
`;

export const useSetupIntentClientSecret = (options: {
  userId?: string;
  organizationId?: string;
  skip: boolean;
}): {
  clientSecret?: string;
  isLoading: boolean;
} => {
  const { userId, organizationId, skip } = options;
  const [
    getSetupIntentClientSecretForUser,
    getSetupIntentClientSecretForUserResult,
  ] = useMutation<
    useSetupIntentClientSecret_GetSetupIntentClientSecretForUser,
    useSetupIntentClientSecret_GetSetupIntentClientSecretForUserVariables
  >(GET_SETUP_INTENT_CLIENT_SECRET_FOR_USER);

  const [
    getSetupIntentClientSecretForOrganization,
    getSetupIntentClientSecretForOrganizationResult,
  ] = useMutation<
    useSetupIntentClientSecret_GetSetupIntentClientSecretForOrganization,
    useSetupIntentClientSecret_GetSetupIntentClientSecretForOrganizationVariables
  >(GET_SETUP_INTENT_CLIENT_SECRET_FOR_ORGANIZATION);

  React.useEffect(() => {
    const getSetupIntentClientSecret = async () => {
      if (userId) {
        await getSetupIntentClientSecretForUser({
          variables: { input: { userId } },
        });
      }

      if (organizationId) {
        await getSetupIntentClientSecretForOrganization({
          variables: { input: { organizationId } },
        });
      }
    };
    if (!skip) {
      getSetupIntentClientSecret();
    }
  }, [
    getSetupIntentClientSecretForOrganization,
    getSetupIntentClientSecretForUser,
    organizationId,
    userId,
    skip,
  ]);

  const isLoading = skip
    ? false
    : !getSetupIntentClientSecretForUserResult.called &&
      !getSetupIntentClientSecretForOrganizationResult.called
    ? true
    : getSetupIntentClientSecretForUserResult.loading ||
      getSetupIntentClientSecretForOrganizationResult.loading;

  const clientSecret =
    getSetupIntentClientSecretForUserResult.data
      ?.getSetupIntentClientSecretForUser.clientSecret ||
    getSetupIntentClientSecretForOrganizationResult.data
      ?.getSetupIntentClientSecretForOrganization.clientSecret;

  return { clientSecret, isLoading };
};
